import { Link } from 'react-router-dom';
import { Flag } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SoftFlagRow } from '@/hooks/usePortfolioRisk';

// ─── Types ──────────────────────────────────────────────────────────────────

interface SoftFlagSummaryWidgetProps {
  rows: SoftFlagRow[];
  loading?: boolean;
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function WidgetSkeleton() {
  return (
    <div role="status" aria-label="Loading soft flag summary data" className="space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-8 animate-pulse rounded bg-muted" aria-hidden="true" />
      ))}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Soft Flag Summary widget — FR-086 Widget 4.
 * Aggregated counts of soft flags across the portfolio.
 * Blue accent. Each row links to the filtered contracts list for that flag type.
 */
export function SoftFlagSummaryWidget({ rows, loading }: SoftFlagSummaryWidgetProps) {
  const totalFlags = rows.reduce((acc, r) => acc + r.count, 0);

  return (
    <section
      className="rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950 p-4 space-y-3"
      aria-label="Soft flag summary across portfolio"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Flag className="h-4 w-4 text-blue-600 dark:text-blue-400" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">Soft Flags</h3>
        </div>
        <span
          className="rounded-full bg-blue-100 dark:bg-blue-900 px-2 py-0.5 text-xs font-bold text-blue-700 dark:text-blue-300"
          aria-live="polite"
          aria-label={`${totalFlags} total soft flags across portfolio`}
        >
          {totalFlags}
        </span>
      </div>

      {loading ? (
        <WidgetSkeleton />
      ) : rows.length === 0 ? (
        <p className="text-xs text-blue-700 dark:text-blue-300 italic">No soft flags detected.</p>
      ) : (
        <ul className="space-y-1.5" role="list" aria-label="Soft flag categories">
          {rows.map((row) => (
            <li key={row.flag_key} className="flex items-center justify-between gap-2">
              <Link
                to={`/contracts?soft_flag=${encodeURIComponent(row.flag_key)}`}
                className={cn(
                  'truncate text-xs text-blue-800 dark:text-blue-200 underline hover:no-underline',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded',
                )}
                title={row.label}
              >
                {row.label}
              </Link>
              <span className="shrink-0 text-xs font-medium text-blue-600 dark:text-blue-400">
                {row.count}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
